// The signaling client — docs/02 §4 (WS protocol), docs/04 §2 (endpoint
// racing), docs/01 §5.3 (resume). Owns exactly one live WebSocket:
//
//  - connect: last-known-good endpoint first, then race the rest with 4 s
//    stagger (happy-eyeballs style); first successful AUTH wins.
//  - first frame is `sys.auth` (never credentials in the URL); a connection
//    only counts once `sys.hello` arrives.
//  - 25 s sys.ping heartbeat for RTT/liveness; while foregrounded 2 missed
//    pongs ⇒ reconnect, but while hidden (JS throttled) missed pongs never
//    self-close — the server keeps the socket alive with protocol pings, and we
//    re-probe liveness on wake (visibilitychange / host nudge) instead.
//  - resume: `connId` + last processed `seq` let the server replay missed
//    frames after a flap, so an ICE restart can complete across it.
//  - signed endpoint-list updates: verified (keyring + strictly-greater
//    version) before replacing the racing list.
//  - DoH probe: after two rounds in which not one socket even opened, ask a
//    public resolver whether our own name still resolves (docs/04 §1) — the
//    only way this client can tell a poisoned name from a refused connection.
//
// Liveness rule for every path in here: `stop()` is the only way to stop
// trying. A refused session (4001/4403) re-authenticates with a fresh one and
// keeps racing (sessionRejected); everything else backs off and retries
// forever. If the server is reachable, this client's job is to be connected.

import {
  MAX_FRAME_BYTES,
  SysRpcPayload,
  TIMEOUTS,
  WS_CLOSE,
  WsFrame,
  type EndpointList,
  type SysHelloPayload,
} from '@merkury/protocol';
import type {
  AccessTokenProvider,
  SignalingStatus,
  TimerHost,
  WebSocketFactory,
  WebSocketLike,
} from '../types.js';
// Type-only (erased at build): the REST client owns the vocabulary of a
// tunnelled request, this module owns the transport that carries it.
import type { RpcFailure, RpcOutcome, RpcRequest, RpcTransport } from '../../api/client.js';
import { verifyEndpointList, type Keyring, type VerifyResult } from '../crypto/endpointList.js';
import { lookupViaDoh, type DohFetch, type DohOutcome } from './doh.js';
import { utf8 } from '../bytes.js';

export const RACE_STAGGER_MS = 4_000;
export const ATTEMPT_TIMEOUT_MS = 10_000;
export const BACKOFF_BASE_MS = 1_000;
export const BACKOFF_CAP_MS = 30_000;
/**
 * A won connection that dies younger than this is a flap. One flap still
 * re-races immediately — an ICE restart must complete across it (Connection >
 * Latency). But CONSECUTIVE flaps mean something is accepting the session and
 * instantly dropping it (an overloaded node, a middlebox with an axe), and
 * re-racing with zero delay against that is a tight connect/close loop. From
 * the second consecutive flap the reconnect goes through the normal jittered
 * backoff, seeded from the streak so it grows instead of restarting at the
 * base every cycle.
 */
export const FLAP_WINDOW_MS = 10_000;
/** Outbound frames buffered while disconnected (flushed after auth). */
export const SEND_BUFFER_LIMIT = 64;
/**
 * How long a wake-up liveness probe waits for a sys.pong before deciding the
 * socket rotted while backgrounded and reconnecting. Short relative to the 25 s
 * heartbeat so an incoming call.invite is picked up promptly on screen-on, but
 * generous enough not to abandon a merely slow (still-alive) link.
 */
export const LIVENESS_PROBE_MS = 3_000;

// --- REST over the live socket (protocol ws.ts `sys.rpc`, docs/10 §5) -------
//
// The socket that is already open is the one transport a censor enforcing at
// flow SETUP cannot touch: it needs no handshake, opens no second connection to
// a policed host, and carries a few hundred bytes instead of a fresh TLS
// exchange. In the field incident it stayed up for minutes while every HTTPS
// request died, so a REST call that rides it is the difference between a usable
// app and a green dot over nothing.
//
// It is also the socket a CALL is on. Everything below is shaped by that: only
// a handful of small requests may occupy it at a time, none of them may be
// written while the link is already congested, and a signaling frame is never
// queued behind any of them (see `send`, which writes straight through).

/**
 * How long a tunnelled request waits for its `sys.rpc.result`. Deliberately
 * short: the socket is established and authenticated, so the answer costs one
 * round trip — if it is not back inside this, the socket is not carrying it and
 * the HTTPS fallback (with its own escalating budget) is the better remaining
 * chance. It is the FIRST leg of a chain the Android bridge reaps at 30 s
 * (lib/http.ts), which is the other reason it may not be generous: the fallback
 * has to fit behind it.
 */
export const RPC_DEADLINE_MS = 6_000;

/** Tunnelled requests in flight at once. A call shares this socket, so REST
 *  may occupy only a small, bounded slice of it. Deliberately below the
 *  server's own per-connection admission cap (backend ws/frame.go
 *  `MaxRPCInFlight` = 8), so the gateway's 429 — which is a refusal to
 *  dispatch, not a route's answer — is unreachable from this client and every
 *  429 the caller sees is genuinely the route's. */
export const RPC_MAX_INFLIGHT = 4;

/**
 * Written-but-unanswered tunnelled bytes. The inflight CAP counts requests; on
 * a host that reports no `bufferedAmount` (the native brains' BridgedSocket)
 * this is the only thing that bounds how many REST bytes a call's ICE frame can
 * end up behind, so it is enforced everywhere rather than only where the host
 * happens to expose its queue.
 */
export const RPC_MAX_INFLIGHT_BYTES = 16 * 1024;

/** Requests waiting for a slot. Past this the transport declines — never
 *  written, therefore safe to issue over HTTPS — instead of growing a queue
 *  whose entries would each still be holding a caller. */
export const RPC_QUEUE_LIMIT = 16;

/** Outbound bytes already queued on the socket (when the host exposes
 *  `bufferedAmount`) above which no further RPC frame is written. The link is
 *  busy, and what it is busy with may be a call's audio path. */
export const RPC_BACKPRESSURE_BYTES = 16 * 1024;

/** Re-check cadence for a queue held back by backpressure or by the inflight
 *  cap — nothing waits on a result that may never come. */
export const RPC_DRAIN_MS = 25;

/**
 * Largest tunnelled REQUEST frame. `MAX_FRAME_BYTES` is 64 KB, but a frame that
 * big is precisely what would push a call's ICE behind it, and the whole point
 * of this transport is that a request is CHEAPER than a handshake. Anything
 * larger stays on HTTPS.
 */
export const RPC_MAX_REQUEST_BYTES = 8 * 1024;

/**
 * Consecutive tunnelled requests that die on a LIVE socket before the transport
 * stands down for RPC_COOLDOWN_MS. A server one deploy behind answers no
 * `sys.rpc` at all; without this, every REST call on a perfectly healthy
 * network would pay RPC_DEADLINE_MS before falling back, forever.
 */
export const RPC_FAIL_STREAK = 2;
export const RPC_COOLDOWN_MS = 60_000;

/**
 * How often a LIVE socket re-reads the session's access token to see whether a
 * newer one exists, and installs it with `sys.reauth` (protocol ws.ts) if so.
 *
 * The socket presents its credential ONCE, at `sys.auth`, and the server runs
 * every tunnelled request through the real auth middleware with that same token
 * (backend rpcws `Do` replays it as the Bearer). Access tokens last 15 minutes
 * and a socket has no maximum lifetime, so without this every connection older
 * than its token answers 401 to `/me`, `/friends`, everything — while signaling
 * on the very same socket keeps working perfectly, because that was authorized
 * once. It would hit every user, not only blocked ones.
 *
 * A minute is cheap: the provider answers from its cache until the token is
 * inside its own refresh skew, so this costs a string comparison until the one
 * check where it costs a renewal.
 */
export const REAUTH_CHECK_MS = 60_000;

/**
 * How long after writing a `sys.reauth` a bare `error` frame is read as this
 * server refusing THAT frame.
 *
 * A server one deploy behind answers any unknown frame type with a bare `error`
 * and keeps the socket (backend ws `dispatch` default), and the engine reads a
 * bare error that lands while it is dialing as the server refusing our invite
 * (engine.ts). A frame this client invented must not be able to end a call — so
 * for one round trip after writing one, the error is claimed here and the
 * connection stops offering reauth at all. Same trade, and the same reasoning,
 * as claimRpcError.
 */
export const REAUTH_ERROR_WINDOW_MS = 2_000;

/** The outbound `sys.rpc` frame, straight off the protocol union — this module
 *  never restates a wire shape (CLAUDE.md). */
type RpcFrame = Extract<WsFrame, { type: 'sys.rpc' }>;
type RpcResultPayload = Extract<WsFrame, { type: 'sys.rpc.result' }>['payload'];
type CallSyncPayload = Extract<WsFrame, { type: 'call.sync' }>['payload'];

/**
 * Frame types that mean a call is being set up or is up on this socket, and the
 * ones that end one. Both directions count: the transport has to know a call is
 * happening BEFORE the invite is written, and the only place that knows that
 * without asking the engine is the frame passing through here.
 */
const CALL_LIVE_FRAMES: ReadonlySet<string> = new Set([
  'call.invite',
  'call.ringing',
  'call.accept',
  'call.ice',
  'call.renegotiate',
]);
const CALL_OVER_FRAMES: ReadonlySet<string> = new Set(['call.end', 'call.decline']);

/** `callId` of a frame payload, for the outbound side where the payload is
 *  still `unknown` (OutboundFrame). */
function callIdOf(payload: unknown): string | null {
  const id = (payload as { callId?: unknown } | null)?.callId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/** A request this transport refused before writing anything. `sent: false` is
 *  the whole content of that promise, and it is the only ground on which the
 *  caller may issue the same request over HTTPS. */
function declined(failure: RpcFailure): RpcOutcome {
  return { ok: false, failure, sent: false };
}

interface PendingRpc {
  id: string;
  frame: string;
  /** Measured once, when the frame is built (see RPC_MAX_REQUEST_BYTES). */
  bytes: number;
  settle: (outcome: RpcOutcome) => void;
  timer: unknown;
  /**
   * The frame reached `ws.send`. The ONLY fact replay safety may be decided on:
   * once written, the wire cannot tell a lost response from a lost request, so
   * a non-idempotent request must never be issued again (api/client.ts
   * `mayReissueOverHttps`).
   */
  sent: boolean;
  settled: boolean;
}

/**
 * Per-connection traffic counters — the `ws.lifetime` event of docs/10 §2.
 *
 * Russian carriers are documented to freeze foreign-datacenter TLS flows after
 * roughly 15–20 KB with no RST (docs/04 §1). Whether OUR sockets die near that
 * threshold is not knowable from a blog post; it is knowable from this counter.
 */
export interface WsConnectionStats {
  /** Public endpoint hostname, or `[ip]`/`[host]`. Never an address. */
  host: string;
  /** How long the connection has been alive, or lived, in ms. */
  ageMs: number;
  framesIn: number;
  framesOut: number;
  bytesIn: number;
  bytesOut: number;
  /** WS close code once it ended; null while it is live. */
  closeCode: number | null;
  /** True while this connection is the one carrying traffic. */
  live: boolean;
}

interface ConnCounters {
  host: string;
  startedAt: number;
  endedAt: number | null;
  closeCode: number | null;
  framesIn: number;
  framesOut: number;
  bytesIn: number;
  bytesOut: number;
}

/**
 * UTF-8 byte length without encoding the string. Every frame in and out goes
 * through this, ICE traffic included, so it allocates nothing: one pass, one
 * integer add per code unit. (`utf8()` builds a Uint8Array, which is fine for
 * the once-per-frame size guard in `send` and wrong for a counter.)
 */
function utf8Bytes(s: string): number {
  let n = s.length;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c < 0x80) continue;
    if (c < 0x800) n += 1;
    else if (c >= 0xd800 && c <= 0xdbff) {
      n += 2; // surrogate pair: 4 bytes across 2 units, both already counted once
      i += 1;
    } else n += 2;
  }
  return n;
}

/**
 * How many DISTINCT freshly-minted sessions the server may reject before the
 * client stops presenting `reconnecting` and admits `revoked`. Counting
 * distinct tokens (not rejections) is what makes this bound meaningful: every
 * racer in a round carries the same token, and a token provider that hands back
 * the same cached one is not evidence of anything — only a *new* session that
 * is also refused says "this device is not allowed in".
 */
export const REAUTH_MAX_ROUNDS = 3;

/**
 * Re-auth cadence once the client has settled into `revoked`. Slow enough that
 * a genuinely terminated account cannot hot-loop the auth endpoint (≈12
 * requests/hour, below the 30 challenges/h rate limit), but the state is still
 * not a dead end — and foreground/wake/"retry now" nudges probe immediately.
 */
export const REVOKED_RECHECK_MS = 5 * 60_000;

/**
 * Consecutive lost race rounds in which NOT ONE racer opened a socket, before a
 * DoH probe is worth its round trip (docs/04 §1). Two, never one: a single dead
 * round is an ordinary blip (radio handoff, a cell waking up), and a socket that
 * opened — even one the server then closed with 4403 — is proof the name
 * resolved, so a flap can never reach this count at all.
 */
export const DNS_PROBE_AFTER_ROUNDS = 2;

/** Floor between probes, so an hour-long outage costs one DNS query a minute
 *  instead of one per round. */
export const DNS_PROBE_COOLDOWN_MS = 60_000;

/**
 * What the client knows about whether its own domains resolve.
 *
 * `blocked` and `unknown` are both failures and mean opposite things: the first
 * says a working link answered "that name has no address" (the domain is what
 * the censor took), the second says not one resolver answered — which is
 * precisely the state of knowing nothing, and must not be dressed up as
 * "offline". Under a national firewall "the public DoH resolvers are blocked
 * too" is likelier than "this device has no internet", and those call for
 * opposite responses. `ok` is the one that reads as a failure and is not — the
 * name resolves, so what is killing the connection is the transport (SNI
 * filtering, an IP block, a frozen TLS flow), not DNS. Never carries an
 * address: the verdict is a tier.
 */
export type DnsHealth = 'unprobed' | 'ok' | 'blocked' | 'unknown';

const WS_OPEN = 1;

/** Our own close code for a socket we gave up on (no pong): application range,
 *  distinct from every server-sent code, and reported as the `ws.lifetime`
 *  close code so a diagnostics report can tell "we hung up on it" from "it hung
 *  up on us". */
const HEARTBEAT_LOST_CLOSE = 4000;

/**
 * A host safe to name — the ALLOW-list from lib/http.ts, because the endpoint
 * list permits `kind:'ip'` entries and a mislabelled `kind:'domain'` entry can
 * carry a literal address just as easily. Anything that is not a dot-separated
 * name ending in an alphabetic (or `xn--`) TLD fails it, so no octet can reach a
 * log line — or a third-party resolver's query string (probeHost) — by
 * construction.
 */
const LABEL = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';
const DNS_NAME = new RegExp(`^${LABEL}(?:\\.${LABEL})*\\.(?:[a-z]{2,}|xn--[a-z0-9-]{2,})$`, 'i');

/** Second barrier. `probeHost` already refuses to probe anything that is not a
 *  DNS name, so this should never have to replace one — which is exactly why it
 *  stays: the invariant it guards is non-negotiable and costs one regex. */
function logSafeHost(host: string): string {
  if (DNS_NAME.test(host)) return host;
  return host.includes(':') || host.startsWith('[') || /^[0-9.]+$/.test(host) ? '[ip]' : '[host]';
}

/**
 * The DNS verdict, in the only form it may be logged. `answers` is a COUNT: the
 * addresses a resolver hands back are literal IPs, which is precisely what may
 * never reach a log (CLAUDE.md), and the line mirrors into runtime/logRing —
 * `dev.logs` — so the redaction has to happen here, at the source.
 */
function logDns(state: DnsHealth, host: string, answers: number): void {
  const console_ = (
    globalThis as { console?: { info?: (...a: unknown[]) => void; warn?: (...a: unknown[]) => void } }
  ).console;
  const line = `dns ${state}: host=${logSafeHost(host)} answers=${answers}`;
  if (state === 'ok') console_?.info?.(line);
  else console_?.warn?.(line);
}

/** Hostname of a `wss://…` endpoint URL, or null when there is none to read. */
function hostOfWsUrl(url: string): string | null {
  let host: string;
  if (typeof URL !== 'undefined') {
    try {
      host = new URL(url).hostname;
    } catch {
      return null;
    }
  } else {
    // The iOS brain runs in a bare JSContext, which has no URL constructor.
    const match = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(url);
    host = (match?.[1] ?? '').replace(/^[^@]*@/, '').replace(/:\d+$/, '');
  }
  return host === '' ? null : host;
}

/** The host's own `fetch`, wrapped so it keeps its binding (a bare `fetch`
 *  invoked as a method throws "Illegal invocation" in Chromium). */
function globalDohFetch(): DohFetch | null {
  const f = (globalThis as { fetch?: unknown }).fetch;
  if (typeof f !== 'function') return null;
  return (url, init) => (f as typeof fetch)(url, init as RequestInit);
}

/** A client→server frame before the envelope is applied. */
export interface OutboundFrame {
  type: WsFrame['type'];
  payload: unknown;
}

/**
 * Page-visibility surface, injected so tests can drive it. The default adapter
 * reads the real `document`; in a non-DOM host (Node tests without a document)
 * it reports always-visible and never fires.
 */
export interface VisibilityHost {
  /** True when the page/WebView is backgrounded and JS timers are throttled. */
  hidden(): boolean;
  /** Subscribe to visible transitions; returns an unsubscribe function. */
  onVisible(cb: () => void): () => void;
}

/** Real-`document` VisibilityHost; degrades to always-visible with no document. */
export function defaultVisibility(): VisibilityHost {
  const doc: Document | undefined = typeof document !== 'undefined' ? document : undefined;
  return {
    hidden: () => doc?.hidden ?? false,
    onVisible: (cb) => {
      if (!doc) return () => {};
      const handler = (): void => {
        if (!doc.hidden) cb();
      };
      doc.addEventListener('visibilitychange', handler);
      return () => doc.removeEventListener('visibilitychange', handler);
    },
  };
}

/**
 * OS wake / connectivity-resume source, injected so tests can drive it. This is
 * the missing trigger for BUG-3 (JW8 audit §BUG-3): when the machine sleeps and
 * resumes with the tab *already visible*, no `visibilitychange` fires, so the
 * visibility path never re-probes and a socket that rotted across the sleep is
 * only caught by the ~50 s heartbeat rule. An `online` event is the portable
 * signal for that resume — wire it to the same one-shot liveness probe. The
 * default adapter listens to `window`'s `online`; in a non-DOM host it never
 * fires.
 */
export interface WakeHost {
  /** Subscribe to wake/resume nudges; returns an unsubscribe function. */
  onWake(cb: () => void): () => void;
}

/** Real-`window` WakeHost (fires on `online`); degrades to never with no window. */
export function defaultWake(): WakeHost {
  const win: Window | undefined = typeof window !== 'undefined' ? window : undefined;
  return {
    onWake: (cb) => {
      if (!win) return () => {};
      win.addEventListener('online', cb);
      return () => win.removeEventListener('online', cb);
    },
  };
}

export interface SignalingClientDeps {
  /**
   * Session token supplier. Called once per race; after the server refuses the
   * session it is called with `{forceFresh:true}`, which asks the provider to
   * bypass its cache and mint a new session (refresh, else identity-key login)
   * — see sessionRejected. Providers that ignore the hint still recover, just
   * on their own expiry schedule.
   */
  getToken: AccessTokenProvider;
  ws: WebSocketFactory;
  timers: TimerHost;
  keyring: Keyring;
  /** The verified endpoint list to race (baked-in floor or persisted copy). */
  initialList: EndpointList;
  lastGoodUrl?: string | undefined;
  /** Called when the list or last-known-good changes, for persistence. */
  persist?: (state: { list: EndpointList; lastGoodUrl?: string }) => void;
  onFrame(frame: WsFrame): void;
  onStatus(status: SignalingStatus): void;
  /** Injectable RNG for backoff jitter (tests pin it). */
  random?: () => number;
  /** Page-visibility source; defaults to the real `document`. */
  visibility?: VisibilityHost;
  /** OS wake/resume source (BUG-3); defaults to the real `window` `online`. */
  wake?: WakeHost;
  /**
   * HTTPS transport for DoH probes (docs/04 §1). Defaults to the host's own
   * `fetch` — this probe is the documented answer to DNS poisoning and spent
   * a release being reachable from nowhere, so it must not depend on a host
   * remembering to wire a seam. Pass one to route it through the resilient
   * fetch; with neither, the probe is a no-op.
   */
  dohFetch?: DohFetch;
}

interface Attempt {
  ws: WebSocketLike;
  url: string;
  /** Session token this attempt will authenticate with (never logged). */
  token: string;
  timeout: unknown;
  settled: boolean;
  /** Traffic counters for THIS socket, from the moment it was dialled — the
   *  handshake and `sys.auth` are part of the flow a carrier is metering, so
   *  they belong in the total. Promoted to the connection's counters on a win. */
  counters: ConnCounters;
  /** Socket is open and waiting its turn to send `sys.auth` (see authNext). */
  pendingAuth?: boolean;
  /** `sys.auth` has been sent on this socket and no verdict has come back. */
  authSent?: boolean;
}

export class SignalingClient {
  private status: SignalingStatus = 'idle';
  private list: EndpointList;
  private lastGoodUrl: string | undefined;
  private socket: WebSocketLike | null = null;
  private attempts: Attempt[] = [];
  private pendingStaggers: unknown[] = [];
  private raceRound = 0;
  /** Bumped per race (and by cancelRace) so a stale startRace can't launch. */
  private raceGen = 0;
  private everConnected = false;
  private stopped = true;
  private retryTimer: unknown = null;
  /** timers.now() when the current connection won its race. */
  private wonAt = 0;
  /** Consecutive won connections that died younger than FLAP_WINDOW_MS. */
  private flapStreak = 0;

  /** Latest DNS verdict; read by the host's dev-mode transport diagnostics. */
  private dns: DnsHealth = 'unprobed';
  /** Consecutive lost rounds in which no racer ever opened a socket. */
  private deadRounds = 0;
  /** Whether any socket in the round now running reached `onopen`. */
  private roundOpened = false;
  /** timers.now() of the last DoH probe, for the cooldown. */
  private dnsProbeAt = Number.NEGATIVE_INFINITY;
  private dnsProbing = false;
  /**
   * Bumped by `stop()`. A probe in flight when the user signs out must not come
   * back minutes later to log a line and publish a verdict for a session that no
   * longer exists — `stop()` is the one place this class promises is terminal.
   */
  private dnsProbeGen = 0;

  /**
   * Session-rejection bookkeeping (see sessionRejected). `rejectedToken` is the
   * last token the server refused — held only to tell "the provider minted a new
   * session and that one was refused too" (real evidence) from "the provider
   * handed back the same cached token" (no new information). Never logged.
   */
  private authRejects = 0;
  private rejectedToken: string | null = null;
  /** Ask the provider to bypass its cache on the next race. */
  private forceFreshToken = false;
  /**
   * True once REAUTH_MAX_ROUNDS fresh sessions have been refused: the client
   * keeps probing, but slowly, and reports `revoked` rather than flickering
   * `reconnecting` over a session the server has consistently killed.
   */
  private revokedLane = false;
  /**
   * The credential the live socket is acting under: what it authenticated with
   * at `sys.auth`, or the newer one installed since by `sys.reauth`. Read by the
   * 4001 path and by the credential sync. Never logged.
   */
  private liveToken: string | null = null;

  private connId: string | null = null;
  private lastSeq = 0;
  private outstandingPings = 0;
  private heartbeat: unknown = null;
  private sendBuffer: OutboundFrame[] = [];

  /** Counters for the live connection, and for the last one that ended (which
   *  is what a `ws.lifetime` report is made of — docs/10 §2). */
  private conn: ConnCounters | null = null;
  private lastConn: ConnCounters | null = null;

  /** Tunnelled REST (see the RPC constants above). */
  private transport: RpcTransport | null = null;
  private readonly rpcPending = new Map<string, PendingRpc>();
  private rpcQueue: PendingRpc[] = [];
  private rpcInflight = 0;
  /** Bytes of the written-but-unanswered ones (RPC_MAX_INFLIGHT_BYTES). */
  private rpcInflightBytes = 0;
  private rpcSeq = 0;
  private rpcDrainTimer: unknown = null;
  private rpcDraining = false;
  /** Consecutive tunnelled failures on a live socket, and the deadline until
   *  which the transport stands down after too many (see RPC_FAIL_STREAK). */
  private rpcFails = 0;
  private rpcColdUntil = Number.NEGATIVE_INFINITY;
  /** Endpoint the stand-down was earned against. A cooldown is evidence about
   *  the server behind ONE url — a different endpoint may well be a different
   *  deployment, and inheriting the verdict would leave it untried. */
  private rpcColdFor: string | null = null;
  /** Url of the live connection (comparison only — never logged, never
   *  persisted from here; `lastGoodUrl` owns persistence). */
  private rpcConnUrl: string | null = null;
  /** This connection answered a `sys.rpc.result` at least once, so the server
   *  demonstrably routes the frame (see claimRpcError). */
  private rpcProven = false;
  /** Calls this socket is carrying, by callId — REST stays off a socket that is
   *  setting up or holding a call (prime directive). Per connection: `call.sync`
   *  arrives on every connect and is authoritative, so a missed end frame costs
   *  one connection's worth of tunnelling, never a permanently dark transport. */
  private readonly activeCalls = new Set<string>();

  /** timers.now() of the last credential check on this connection. */
  private authCheckAt = 0;
  /** The credential check in flight, shared by the heartbeat and by the tunnel's
   *  own 401 recovery so the two can never put two reauth frames on the wire. */
  private authSync: Promise<boolean> | null = null;
  /** This connection's server understands `sys.reauth`, until it says otherwise
   *  (claimReauthError). Per connection: the race can land on any deployment. */
  private reauthSupported = true;
  /** Deadline until which a bare `error` frame is attributed to a `sys.reauth`
   *  this client just wrote (REAUTH_ERROR_WINDOW_MS). */
  private reauthErrorUntil = Number.NEGATIVE_INFINITY;

  private readonly visibility: VisibilityHost;
  private visibilityUnsub: (() => void) | null = null;
  private readonly wake: WakeHost;
  private wakeUnsub: (() => void) | null = null;
  /** Wake-up liveness probe timer (null when no probe is in flight). */
  private livenessTimer: unknown = null;
  /** Set by any inbound sys.pong; the probe reads it to decide reachability. */
  private pongSinceProbe = false;

  constructor(private readonly deps: SignalingClientDeps) {
    this.list = deps.initialList;
    this.lastGoodUrl = deps.lastGoodUrl;
    this.visibility = deps.visibility ?? defaultVisibility();
    this.wake = deps.wake ?? defaultWake();
  }

  get currentStatus(): SignalingStatus {
    return this.status;
  }

  get listVersion(): number {
    return this.list.version;
  }

  get endpointList(): EndpointList {
    return this.list;
  }

  /**
   * What the last DoH probe (or a domain endpoint that actually connected)
   * established about our own names — for the dev-mode transport snapshot and
   * the log. A tier, never an address.
   */
  get dnsHealth(): DnsHealth {
    return this.dns;
  }

  /**
   * Traffic on the connection carrying frames right now, or null when there is
   * none. Reset per connection by construction — the counters belong to the
   * socket, not to the client — so a reconnect starts from zero and the numbers
   * describe one TLS flow, which is the only way they can say anything about a
   * flow that dies at a fixed volume (docs/10 §2).
   */
  get socketStats(): WsConnectionStats | null {
    return this.conn === null ? null : this.statsOf(this.conn, true);
  }

  /** The connection that ended most recently, close code included — the
   *  `ws.lifetime` record the diagnostics ring reports. */
  get lastSocketStats(): WsConnectionStats | null {
    return this.lastConn === null ? null : this.statsOf(this.lastConn, false);
  }

  /**
   * REST over this socket (protocol ws.ts `sys.rpc`). Handed to `RestClient` at
   * wiring time; it prefers this transport whenever `ready()` and falls back to
   * HTTPS otherwise.
   */
  get rpcTransport(): RpcTransport {
    this.transport ??= {
      ready: () => this.rpcReady(),
      send: (request) => this.sendRpc(request),
      renewAuth: () => this.syncSocketAuth(),
    };
    return this.transport;
  }

  private statsOf(c: ConnCounters, live: boolean): WsConnectionStats {
    return {
      host: c.host,
      ageMs: (c.endedAt ?? this.deps.timers.now()) - c.startedAt,
      framesIn: c.framesIn,
      framesOut: c.framesOut,
      bytesIn: c.bytesIn,
      bytesOut: c.bytesOut,
      closeCode: c.closeCode,
      live,
    };
  }

  start(): void {
    if (!this.stopped) {
      // Already running. A host re-issuing start() while we sit in the slow
      // revoked lane means "try again now" — it must not be swallowed.
      if (this.revokedLane) this.retryNow();
      return;
    }
    this.stopped = false;
    this.raceRound = 0;
    this.flapStreak = 0;
    this.deadRounds = 0;
    this.resetAuthRejects();
    // Coming back to the foreground (or a host wake nudge, see retryNow) is
    // when a socket that rotted while throttled must be re-checked.
    if (this.visibilityUnsub === null) {
      this.visibilityUnsub = this.visibility.onVisible(() => this.retryNow());
    }
    // BUG-3: a sleep/resume (or Wi-Fi↔LTE handoff) that keeps the tab visible
    // fires no visibilitychange, so the visibility path above never re-probes.
    // The `online`/wake nudge forces the same one-shot probe — a healthy socket
    // answers and nothing changes; a rotted one is caught in LIVENESS_PROBE_MS.
    if (this.wakeUnsub === null) {
      this.wakeUnsub = this.wake.onWake(() => this.retryNow());
    }
    this.setStatus(this.everConnected ? 'reconnecting' : 'connecting');
    void this.startRace();
  }

  /**
   * Stop for good — the ONLY terminal state, and the only one the user asked
   * for: host teardown / sign-out (headless main.ts `teardownSession`). It drops
   * the visibility and wake subscriptions, so nothing in here can resurrect a
   * session the user just signed out of; only an explicit `start()` restarts.
   */
  stop(): void {
    this.stopped = true;
    this.revokedLane = false;
    this.cancelRace();
    this.cancelDnsProbe();
    this.stopHeartbeat();
    if (this.visibilityUnsub !== null) {
      this.visibilityUnsub();
      this.visibilityUnsub = null;
    }
    if (this.wakeUnsub !== null) {
      this.wakeUnsub();
      this.wakeUnsub = null;
    }
    if (this.retryTimer !== null) {
      this.deps.timers.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      s.onclose = null;
      s.close(1000, 'client stop');
    }
    this.endConnection(1000);
    this.setStatus('idle');
  }

  /**
   * Manual "Retry now" / host wake nudge (Home screen, or the shell's
   * screen-on / network-change handler via engine.retrySignaling): abandon any
   * pending backoff and race the endpoint list again immediately, with the
   * backoff counter reset. When already connected this is instead a liveness
   * probe — the socket may have rotted while backgrounded — not a no-op.
   *
   * It is NOT a no-op while `revoked`: that state is a slow lane, not a dead
   * end, and a user asking to retry is exactly the moment to spend one re-auth
   * probe. The rejection counter is deliberately NOT reset here — evidence that
   * the server refused N fresh sessions does not expire because a screen turned
   * on — so a revoked session costs one attempt per nudge, never a burst.
   * Also the foreground/OS-wake handler (see start): when a nudge arrives while
   * disconnected, racing NOW beats sitting out a backoff that can be 30 s long
   * (or a 5 min revoked re-check) — the world just changed under us (screen on,
   * Wi-Fi back, sleep over), and idling on a timer while the network is healthy
   * is precisely the state this client must never be in.
   *
   * Only `stop()` (host teardown) makes this a no-op.
   */
  retryNow(): void {
    if (this.stopped) return;
    if (this.status === 'connected') {
      this.checkLiveness();
      return;
    }
    // The world changed, so the next round must not sit out a long backoff.
    if (!this.revokedLane) this.raceRound = 0;
    // A race already in flight IS the answer to the nudge — leave it alone.
    // Hosts fire wake/online/visibility events in bursts, and restarting the
    // race on each one would keep cancelling handshakes before they can finish;
    // on a slow link (EDGE, where a handshake outlives the 4 s stagger) that
    // turns a nudge storm into a livelock. Only a client parked on a backoff
    // timer needs the kick.
    if (this.attempts.length > 0 || this.pendingStaggers.length > 0) return;
    if (this.retryTimer !== null) {
      this.deps.timers.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    void this.startRace();
  }

  /**
   * Send a frame; buffers (bounded) while disconnected so signaling that
   * races a socket flap — e.g. an ICE-restart renegotiation — completes
   * after resume instead of being lost.
   */
  send(frame: OutboundFrame): void {
    // Before the write, so a queued REST frame is evicted rather than written
    // ahead of the invite that is about to go out.
    this.noteCallFrame(frame.type, frame.payload);
    const json = JSON.stringify({ type: frame.type, seq: 0, payload: frame.payload });
    // An over-limit frame is closed on with 4413 by the server, and buffering it
    // would re-send it after every reconnect — one poison frame, a permanent
    // connect/close loop. Dropping it loses one message; keeping the socket is
    // worth more (Connection > everything). The cheap UTF-16-length screen is
    // exact for anything under a third of the limit (UTF-8 never exceeds 3 bytes
    // per UTF-16 unit); only a genuinely large frame is measured.
    if (json.length * 3 > MAX_FRAME_BYTES && utf8(json).length > MAX_FRAME_BYTES) return;
    if (this.socket && this.socket.readyState === WS_OPEN && this.status === 'connected') {
      // Straight through, ahead of anything REST has waiting: a call's ICE and
      // answer frames may never queue behind tunnelled requests (see drainRpc,
      // which is the only thing that ever holds a write back).
      this.socket.send(json);
      this.countOut(this.conn, json);
      return;
    }
    if (this.sendBuffer.length >= SEND_BUFFER_LIMIT) this.sendBuffer.shift();
    this.sendBuffer.push(frame);
  }

  /**
   * Offer a (possibly hostile) endpoint-list document from any distribution
   * channel. Adopted and persisted only with a valid pinned signature and a
   * strictly greater version.
   */
  async updateEndpointList(input: unknown): Promise<VerifyResult> {
    const result = await verifyEndpointList(input, {
      keyring: this.deps.keyring,
      currentVersion: this.list.version,
    });
    if (result.ok) {
      this.list = result.document;
      // The verdict was evidence about the OLD names. A freshly signed list is
      // the healing channel's entire payload — its domains are the ones nobody
      // has tested yet — and a stale `blocked` would demote every one of them
      // behind the pinned IPs on the first race after adoption, i.e. penalise
      // exactly the endpoints that were shipped to be tried (docs/04 §2).
      this.dns = 'unprobed';
      this.persist();
      // A newly adopted list is the one piece of evidence that a reachable
      // endpoint may exist where the old list had none — the whole point of
      // shipping it (docs/04 §2). Sitting out the remaining backoff on it is
      // exactly the app-side stall this client must not have.
      if (!this.stopped && this.status !== 'connected') this.retryNow();
    }
    return result;
  }

  // --- connection racing ----------------------------------------------------

  private orderedUrls(): string[] {
    const urls = this.list.endpoints.map((e) => e.url);
    const ordered =
      this.lastGoodUrl && urls.includes(this.lastGoodUrl)
        ? [this.lastGoodUrl, ...urls.filter((u) => u !== this.lastGoodUrl)]
        : urls;
    if (this.dns !== 'blocked') return ordered;
    // A resolver on a working link says our names have no address, so every
    // domain endpoint in the list is unreachable BY CONSTRUCTION — and each one
    // still costs the next racer a 4 s stagger. The pinned ip endpoints need no
    // DNS at all: they go first (Connection > Latency).
    const pinned = new Set(
      this.list.endpoints.filter((e) => e.kind === 'ip').map((e) => e.url),
    );
    if (pinned.size === 0) return ordered;
    return [...ordered.filter((u) => pinned.has(u)), ...ordered.filter((u) => !pinned.has(u))];
  }

  private async startRace(): Promise<void> {
    if (this.stopped) return;
    // Hosts fire wake and visibility nudges together on resume; without a
    // generation stamp each one would start its own race across the await
    // below and double every socket the client opens.
    const gen = ++this.raceGen;
    let token: string;
    try {
      token = await this.deps.getToken(this.forceFreshToken ? { forceFresh: true } : undefined);
      this.forceFreshToken = false;
    } catch {
      // A token failure is indistinguishable from an offline radio here (both
      // are just a rejected promise), so it can never be terminal — back off and
      // ask again. The revoked lane's slower cadence applies if we are in it.
      if (gen === this.raceGen) this.scheduleRetry();
      return;
    }
    if (this.stopped || gen !== this.raceGen) return;

    const urls = this.orderedUrls();
    this.roundOpened = false;
    // Arm the staggered starts BEFORE dialling the first URL. `deps.ws()` can
    // throw synchronously (a browser SecurityError, a scheme the host refuses),
    // and a failure landing while pendingStaggers is still empty looks exactly
    // like the end of the round — counting one round as two, which would fire
    // the DNS probe a full round early on precisely the links where dialling
    // throws.
    urls.forEach((url, i) => {
      if (i === 0) return;
      // eslint-disable-next-line prefer-const
      let handle: unknown;
      handle = this.deps.timers.setTimeout(() => {
        this.pendingStaggers = this.pendingStaggers.filter((h) => h !== handle);
        this.launchAttempt(url, token);
      }, i * RACE_STAGGER_MS);
      this.pendingStaggers.push(handle);
    });
    const first = urls[0];
    if (first !== undefined) this.launchAttempt(first, token);
  }

  private launchAttempt(url: string, token: string): void {
    if (this.stopped || this.status === 'connected') return;
    let ws: WebSocketLike;
    try {
      ws = this.deps.ws(url);
    } catch {
      this.noteAttemptFailed();
      return;
    }
    const attempt: Attempt = {
      ws,
      url,
      token,
      settled: false,
      timeout: this.deps.timers.setTimeout(() => this.failAttempt(attempt), ATTEMPT_TIMEOUT_MS),
      counters: {
        host: logSafeHost(hostOfWsUrl(url) ?? ''),
        startedAt: this.deps.timers.now(),
        endedAt: null,
        closeCode: null,
        framesIn: 0,
        framesOut: 0,
        bytesIn: 0,
        bytesOut: 0,
      },
    };
    this.attempts.push(attempt);

    ws.onopen = () => {
      // Race the CONNECTIONS, authenticate ONE AT A TIME. Every racer carries
      // the same session, and the server registers by sid: a second sys.auth
      // supersedes the first socket (4409), which the client reads as a lost
      // connection and answers by racing again — a permanent reconnect loop on
      // any link where the handshake outruns RACE_STAGGER_MS, i.e. exactly
      // EDGE. Connecting in parallel is what makes racing worth having on a
      // high-RTT link; authenticating in parallel is what breaks it.
      // An open socket is proof the name resolved and the handshake completed,
      // whatever the server says next — the round can no longer be evidence of
      // DNS trouble (see probeDns).
      this.roundOpened = true;
      attempt.pendingAuth = true;
      this.authNext();
    };
    ws.onmessage = (ev) => {
      this.countIn(attempt.counters, ev.data);
      const frame = this.parseFrame(ev.data);
      if (!frame) return;
      if (frame.type === 'sys.hello') {
        this.winAttempt(attempt, frame.payload);
      } else if (frame.type === 'error') {
        this.failAttempt(attempt);
      }
      // anything else pre-hello is out of contract; the timeout will reap it
    };
    ws.onerror = () => this.failAttempt(attempt);
    ws.onclose = (ev) => {
      if (!attempt.settled) {
        // The session, not this endpoint, was refused: 4403 (token invalid,
        // expired, or its sid revoked — backend ws/handler.go closeAuthFailed,
        // which is what a REVOKED session looks like on a NEW socket) and 4001
        // (revoked between auth and hello). Note it so the next race mints a
        // fresh session, but do NOT cancel this race: one hostile or misbehaving
        // endpoint answering 4403 must never stop the other endpoints from being
        // tried — that would hand any single node a client-wide kill switch.
        if (ev.code === WS_CLOSE.AUTH_FAILED || ev.code === WS_CLOSE.SESSION_REVOKED) {
          this.noteAuthRejected(attempt.token);
          this.failAttempt(attempt);
          return;
        }
        if (ev.code === WS_CLOSE.RESUME_FAILED && this.connId !== null) {
          // Stale connId: drop resume state and immediately re-auth fresh to
          // the same endpoint (the server told us exactly what to fix).
          this.connId = null;
          this.lastSeq = 0;
          attempt.settled = true;
          this.deps.timers.clearTimeout(attempt.timeout);
          this.detach(attempt.ws);
          this.attempts = this.attempts.filter((a) => a !== attempt);
          this.launchAttempt(url, token);
          // This attempt held the auth turn; hand it on so an already-open
          // racer doesn't idle until its own 10 s timeout if the relaunch
          // never opens.
          this.authNext();
          return;
        }
        this.failAttempt(attempt);
      }
    };
  }

  /**
   * Send `sys.auth` on the longest-waiting open socket, if none is already
   * awaiting a verdict. Called when a socket opens and again whenever the
   * authenticating attempt settles, so a failed endpoint hands the turn to the
   * next one that has already finished its handshake — no extra round trip.
   */
  private authNext(): void {
    if (this.stopped || this.status === 'connected') return;
    if (this.attempts.some((a) => a.authSent && !a.settled)) return;
    const next = this.attempts.find((a) => a.pendingAuth && !a.settled);
    if (!next) return;
    next.pendingAuth = false;
    next.authSent = true;
    // First frame, within AUTH_DEADLINE_MS: sys.auth (docs/02 §4). agent
    // 'app' = the user is actually IN Merkury (presence green); the native
    // stay-reachable socket authenticates as 'reach' instead.
    const payload: { token: string; resume?: string; lastSeq?: number; agent: 'app' } = {
      token: next.token,
      agent: 'app',
    };
    if (this.connId !== null) {
      payload.resume = this.connId;
      payload.lastSeq = this.lastSeq;
    }
    const json = JSON.stringify({ type: 'sys.auth', seq: 0, payload });
    try {
      next.ws.send(json);
      this.countOut(next.counters, json);
    } catch {
      this.failAttempt(next);
    }
  }

  private failAttempt(attempt: Attempt): void {
    if (attempt.settled) return;
    attempt.settled = true;
    this.deps.timers.clearTimeout(attempt.timeout);
    this.detach(attempt.ws);
    try {
      attempt.ws.close();
    } catch {
      // already closed
    }
    this.attempts = this.attempts.filter((a) => a !== attempt);
    // Hand the auth turn on before deciding the race is over, or a queued open
    // socket would sit unauthenticated until its own attempt timeout.
    this.authNext();
    this.noteAttemptFailed();
  }

  private noteAttemptFailed(): void {
    if (this.attempts.length === 0 && this.pendingStaggers.length === 0 && !this.stopped) {
      this.deadRounds = this.roundOpened ? 0 : this.deadRounds + 1;
      // Arm the reconnect FIRST. The probe is fire-and-forget behind it, so
      // nothing that would have connected ever waits on a DNS round trip.
      this.scheduleRetry();
      this.probeDns();
    }
  }

  /**
   * Ask a public DoH resolver whether our own domain still resolves — the
   * documented answer to DNS poisoning (docs/04 §1), and the only way this
   * client can tell "the name is gone" from "the connection is refused".
   *
   * Deliberately reluctant. It runs only after DNS_PROBE_AFTER_ROUNDS rounds in
   * which no racer even opened a socket, at most once per DNS_PROBE_COOLDOWN_MS,
   * only while a probe is not already in flight, and only for a name we actually
   * intend to dial. With no resolvers configured — `doh: []`, which is the
   * production document today — it returns before touching the network, so a
   * list without DoH pays exactly nothing.
   */
  private probeDns(): void {
    if (this.dnsProbing || this.deadRounds < DNS_PROBE_AFTER_ROUNDS) return;
    const resolvers = this.list.doh ?? [];
    if (resolvers.length === 0) return;
    const host = this.probeHost();
    if (host === null) return; // nothing in this list resolves anything (probeHost)
    const fetchFn = this.deps.dohFetch ?? globalDohFetch();
    if (fetchFn === null) return;
    const now = this.deps.timers.now();
    if (now - this.dnsProbeAt < DNS_PROBE_COOLDOWN_MS) return;
    this.dnsProbeAt = now;
    this.dnsProbing = true;
    void this.runProbe(host, resolvers, fetchFn, ++this.dnsProbeGen);
  }

  /**
   * Abandon any probe in flight. Its result is discarded rather than awaited:
   * `stop()` is sign-out, and a verdict logged after it belongs to nobody.
   */
  private cancelDnsProbe(): void {
    this.dnsProbeGen++;
    this.dnsProbing = false;
  }

  private async runProbe(
    host: string,
    resolvers: readonly string[],
    fetchFn: DohFetch,
    gen: number,
  ): Promise<void> {
    try {
      // Each query carries its own deadline and its own abort (doh.ts), so a
      // resolver that accepts the connection and then goes silent — the
      // documented filtered-link freeze (docs/04 §1) — costs that resolver's
      // deadline and nothing more: the fallbacks behind it are still tried, and
      // the whole lookup is bounded by resolvers × 2 × DOH_QUERY_TIMEOUT_MS.
      const outcome = await lookupViaDoh(host, resolvers, fetchFn, {
        timers: this.deps.timers,
        cancelled: () => gen !== this.dnsProbeGen,
      });
      if (gen !== this.dnsProbeGen) return; // stopped while we were asking
      this.noteDns(outcome, host);
    } catch {
      // lookupViaDoh swallows its own transport failures; anything still
      // thrown here is a broken host fetch and says nothing about DNS.
    } finally {
      if (gen === this.dnsProbeGen) this.dnsProbing = false;
    }
  }

  private noteDns(outcome: DohOutcome, host: string): void {
    // `unreachable` means no resolver answered, which is not a statement about
    // the link — say so, and let the diagnosis stay honest.
    this.dns =
      outcome.kind === 'resolved' ? 'ok' : outcome.kind === 'unresolved' ? 'blocked' : 'unknown';
    logDns(this.dns, host, outcome.kind === 'resolved' ? outcome.addresses.length : 0);
  }

  /**
   * The name to ask about: the endpoint we most want to reach — the last known
   * good one when it is a domain, else the first. `kind:'ip'` endpoints use no
   * DNS, so a list made only of those is never probed.
   *
   * Only a real DNS NAME qualifies. A `kind:'domain'` entry can carry a literal
   * address (the schema permits it, and mislabelling one is a one-character
   * mistake), and asking Cloudflare and Google to resolve `198.51.100.23` would
   * hand two third parties an endpoint address the exposure policy exists to
   * protect (docs/04 §2) — after which the NXDOMAIN it earns would be read as
   * "our names are blocked" and reorder every race on a fiction.
   */
  private probeHost(): string | null {
    const names: Array<{ url: string; host: string }> = [];
    for (const e of this.list.endpoints) {
      if (e.kind !== 'domain') continue;
      const host = hostOfWsUrl(e.url);
      if (host !== null && DNS_NAME.test(host)) names.push({ url: e.url, host });
    }
    const preferred = names.find((e) => e.url === this.lastGoodUrl) ?? names[0];
    return preferred ? preferred.host : null;
  }

  private winAttempt(attempt: Attempt, hello: SysHelloPayload): void {
    if (attempt.settled || this.stopped) return;
    attempt.settled = true;
    this.deps.timers.clearTimeout(attempt.timeout);
    this.attempts = this.attempts.filter((a) => a !== attempt);
    this.cancelRace(); // cancel losers + staggered starts

    if (!hello.resumed) this.lastSeq = 0; // fresh connection: seqs restart
    this.connId = hello.connId;
    this.socket = attempt.ws;
    // This socket's counters ARE the connection's: the handshake and the auth
    // exchange already on them are part of the same flow.
    this.conn = attempt.counters;
    // Which endpoint an RPC stand-down was earned against (rpcCold).
    this.rpcConnUrl = attempt.url;
    this.raceRound = 0;
    this.wonAt = this.deps.timers.now();
    this.everConnected = true;
    this.liveToken = attempt.token;
    this.resetAuthRejects(); // the server accepted this session: evidence void
    this.deadRounds = 0;
    // A domain endpoint that carried a live socket resolved, by observation —
    // better evidence than any probe, and free. It also retires a stale
    // `blocked` verdict, which would otherwise keep reordering the race.
    if (this.list.endpoints.some((e) => e.kind === 'domain' && e.url === attempt.url)) {
      this.dns = 'ok';
    }
    if (this.lastGoodUrl !== attempt.url) {
      this.lastGoodUrl = attempt.url;
      this.persist();
    }

    attempt.ws.onmessage = (ev) => this.handleFrame(ev.data);
    attempt.ws.onerror = () => {
      // onclose follows; nothing to do here
    };
    attempt.ws.onclose = (ev) => this.handleClose(ev.code);

    this.setStatus('connected');
    this.startHeartbeat();
    const buffered = this.sendBuffer;
    this.sendBuffer = [];
    for (const frame of buffered) this.send(frame);
  }

  private cancelRace(): void {
    this.raceGen++; // abandon any startRace still awaiting its token
    for (const handle of this.pendingStaggers) this.deps.timers.clearTimeout(handle);
    this.pendingStaggers = [];
    for (const attempt of [...this.attempts]) {
      attempt.settled = true;
      this.deps.timers.clearTimeout(attempt.timeout);
      this.detach(attempt.ws);
      try {
        attempt.ws.close();
      } catch {
        // already closed
      }
    }
    this.attempts = [];
  }

  private scheduleRetry(): void {
    if (this.stopped || this.retryTimer !== null) return;
    // Enough fresh sessions refused ⇒ settle into the slow lane. This is the
    // closest thing to a terminal state the client has, and it is still a timer,
    // not a dead end.
    if (this.authRejects >= REAUTH_MAX_ROUNDS) this.revokedLane = true;
    this.setStatus(this.everConnected ? 'reconnecting' : 'connecting');
    const random = this.deps.random ?? Math.random;
    const base = this.revokedLane
      ? REVOKED_RECHECK_MS
      : Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** this.raceRound);
    const delay = base * (0.7 + 0.6 * random());
    this.raceRound++;
    this.retryTimer = this.deps.timers.setTimeout(() => {
      this.retryTimer = null;
      void this.startRace();
    }, delay);
  }

  // --- the live connection ---------------------------------------------------

  private handleFrame(data: unknown): void {
    this.countIn(this.conn, data);
    const frame = this.parseFrame(data);
    if (!frame) return;

    switch (frame.type) {
      case 'sys.ping':
        this.send({ type: 'sys.pong', payload: {} });
        return;
      case 'sys.pong':
        this.outstandingPings = 0;
        this.pongSinceProbe = true;
        return;
      case 'sys.revoked':
        this.sessionRejected();
        return;
      case 'sys.hello':
      case 'sys.auth':
        return; // not expected post-auth
      default:
        break;
    }

    // Server frames carry a monotonic seq; drop replays from resume overlap.
    if (frame.seq > 0) {
      if (frame.seq <= this.lastSeq) return;
      this.lastSeq = frame.seq;
    }
    // A tunnelled REST answer belongs to the caller that is awaiting it, not to
    // the call engine: it never reaches onFrame.
    if (frame.type === 'sys.rpc.result') {
      this.resolveRpc(frame.payload);
      return;
    }
    if (frame.type === 'error' && (this.claimReauthError() || this.claimRpcError())) return;
    this.noteCallFrame(frame.type, frame.payload);
    this.deps.onFrame(frame);
  }

  private handleClose(code: number): void {
    this.stopHeartbeat();
    this.endConnection(code);
    this.socket = null;
    if (this.stopped) return;
    if (code === WS_CLOSE.SESSION_REVOKED) {
      this.sessionRejected();
      return;
    }
    if (code === WS_CLOSE.RESUME_FAILED) {
      this.connId = null;
      this.lastSeq = 0;
    }
    const lived = this.deps.timers.now() - this.wonAt;
    this.flapStreak = lived < FLAP_WINDOW_MS ? this.flapStreak + 1 : 0;
    this.setStatus('reconnecting');
    if (this.flapStreak >= 2) {
      // winAttempt zeroes raceRound on every win, so without this seed each
      // accept/instant-close cycle would retry at the base delay forever.
      this.raceRound = this.flapStreak - 1;
      this.scheduleRetry();
      return;
    }
    void this.startRace();
  }

  private startHeartbeat(): void {
    this.outstandingPings = 0;
    // The connection has just presented its credential; the clock to the next
    // check starts here.
    this.authCheckAt = this.deps.timers.now();
    this.heartbeat = this.deps.timers.setInterval(() => {
      // While hidden, the WebView is timer-throttled and JS can't answer pongs
      // promptly — a growing outstandingPings is NOT evidence the socket is
      // dead, so we never self-close. The server keeps us alive via protocol
      // pings (answered by the network stack, no JS), and we re-check liveness
      // on wake (checkLiveness). While visible, keep the classic rule.
      if (this.outstandingPings >= 2 && !this.visibility.hidden()) {
        // Two missed pongs while foregrounded ⇒ socket dead; reconnect.
        this.reconnectDead();
        return;
      }
      this.outstandingPings++;
      this.send({ type: 'sys.ping', payload: {} });
      // The same timer that keeps the socket alive keeps the credential it acts
      // under alive (REAUTH_CHECK_MS) — one liveness rule, both halves of it.
      this.maybeSyncAuth();
    }, TIMEOUTS.HEARTBEAT_MS);
  }

  private maybeSyncAuth(): void {
    const now = this.deps.timers.now();
    if (now - this.authCheckAt < REAUTH_CHECK_MS) return;
    this.authCheckAt = now;
    void this.syncSocketAuth();
  }

  /**
   * Install the session's current access token on the LIVE socket when it is
   * newer than the one that connection is acting under (`sys.reauth`).
   *
   * Resolves true only when a DIFFERENT credential actually reached the wire —
   * the one fact on which a caller may re-issue a request the tunnel refused
   * (api/client.ts). Never rejects: a provider that cannot produce a token is
   * simply "no newer credential", and HTTPS decides from there.
   *
   * Single-flight, because the heartbeat and a tunnelled 401 can ask at the same
   * instant and two frames would install the same token twice.
   */
  private syncSocketAuth(): Promise<boolean> {
    const inflight = this.authSync;
    if (inflight !== null) return inflight;
    const sync = this.runAuthSync()
      .catch(() => false)
      .then((changed) => {
        this.authSync = null;
        return changed;
      });
    this.authSync = sync;
    return sync;
  }

  /**
   * Written only where the tunnel itself would be offered (`rpcReady`), for two
   * reasons that both come from the prime directive: while a call is on this
   * socket the tunnel is closed anyway, so the frame buys nothing; and a server
   * one deploy behind answers an unknown frame with a bare `error` the engine
   * can read as a refusal of an invite (REAUTH_ERROR_WINDOW_MS). Ordering does
   * the rest — frames on one socket are processed in order, so any request
   * written after this one is dispatched under the new credential.
   */
  private async runAuthSync(): Promise<boolean> {
    if (!this.reauthReady()) return false;
    let token: string;
    try {
      token = await this.deps.getToken();
    } catch {
      return false; // no newer credential to be had; the caller falls back
    }
    const socket = this.socket;
    if (socket === null || token === this.liveToken || !this.reauthReady()) return false;
    const json = JSON.stringify({ type: 'sys.reauth', seq: 0, payload: { token } });
    // Armed BEFORE the write, exactly as drainRpc books `sent` before it: a host
    // that delivers this server's answer synchronously from inside send() would
    // otherwise land its refusal before anything was watching for one.
    this.reauthErrorUntil = this.deps.timers.now() + REAUTH_ERROR_WINDOW_MS;
    try {
      socket.send(json);
    } catch {
      this.reauthErrorUntil = Number.NEGATIVE_INFINITY;
      return false;
    }
    this.countOut(this.conn, json);
    // …and if that refusal already came back, nothing was installed: the socket
    // is still acting under the old credential and must not claim otherwise.
    if (!this.reauthSupported) return false;
    this.liveToken = token;
    return true;
  }

  private reauthReady(): boolean {
    return this.reauthSupported && this.rpcReady();
  }

  /**
   * A bare `error` frame arriving within one round trip of a `sys.reauth` we
   * wrote (REAUTH_ERROR_WINDOW_MS). On a server that does not know the frame
   * this IS its answer to it, and the engine would read the same frame as the
   * server refusing an invite. The connection stops offering reauth — its
   * credential then ages out and the tunnel's 401s fall back to HTTPS, which is
   * exactly the behaviour that server already had — and the frame stops here.
   */
  private claimReauthError(): boolean {
    if (this.deps.timers.now() >= this.reauthErrorUntil) return false;
    this.reauthErrorUntil = Number.NEGATIVE_INFINITY;
    this.reauthSupported = false;
    return true;
  }

  /**
   * One-shot liveness probe used on foreground/wake: send a sys.ping and give
   * the socket a short window to answer. Reconnect only if the pong never
   * comes — so a healthy throttled socket is preserved, but a genuinely rotted
   * one is replaced (and resumed) fast enough to catch an incoming call.invite.
   */
  private checkLiveness(): void {
    if (this.status !== 'connected' || !this.socket) return;
    if (this.livenessTimer !== null) return; // a probe is already in flight
    // Clear the stale throttled-window count so the heartbeat interval doesn't
    // reconnect on it the instant we're visible again — the probe decides.
    this.outstandingPings = 0;
    this.pongSinceProbe = false;
    this.send({ type: 'sys.ping', payload: {} });
    this.livenessTimer = this.deps.timers.setTimeout(() => {
      this.livenessTimer = null;
      if (this.status !== 'connected') return;
      if (!this.pongSinceProbe) this.reconnectDead();
    }, LIVENESS_PROBE_MS);
  }

  /** Tear down the current (dead) socket and start reconnecting/resuming. */
  private reconnectDead(): void {
    const s = this.socket;
    this.stopHeartbeat();
    // BEFORE endConnection. Unlike every other teardown path, the socket here is
    // still open and the status is still `connected`, so releasing the pending
    // requests first would re-enter drainRpc (settleRpc frees a slot and drains)
    // with rpcReady() still true — and write the queued frames into the socket
    // we are abandoning. They would then be reported `sent: true`, which is the
    // one fact that forbids the HTTPS fallback: a friend request nobody sent,
    // failed as if it had been.
    this.socket = null;
    this.endConnection(HEARTBEAT_LOST_CLOSE);
    if (s) {
      this.detach(s);
      try {
        s.close(HEARTBEAT_LOST_CLOSE, 'heartbeat lost');
      } catch {
        // already closed
      }
    }
    this.setStatus('reconnecting');
    void this.startRace();
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      this.deps.timers.clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
    if (this.livenessTimer !== null) {
      this.deps.timers.clearTimeout(this.livenessTimer);
      this.livenessTimer = null;
    }
  }

  /**
   * The server terminated the session under a LIVE socket: WS close 4001 or a
   * `sys.revoked` frame. Tear the connection down, drop resume state, and
   * re-authenticate with a fresh session — this is deliberately NOT a dead end.
   *
   * Security judgement (docs/02 §4 "session termination semantics" says the
   * client "returns to the login screen"; this is a considered deviation):
   *
   *  - The wire cannot tell the two causes apart. (a) "the user terminated this
   *    session from another device" and (b) "the sid is stale/expired" both
   *    arrive as 4001 here and as 4403 on the next socket; the server sends no
   *    reason, so any client-side distinction would be invented, not observed.
   *  - Refusing to re-authenticate buys no security. The identity seed is on
   *    this device and the server issues a session to whoever proves it holds
   *    that key — which is exactly what the REST layer already does on a
   *    definitive rejection (BrainAccount.recover ⇒ key login), and what this
   *    process itself does on the next launch. A "terminal" WS state therefore
   *    never PREVENTED the resurrection; it only delayed it until a restart,
   *    while leaving a perfectly healthy app deaf to incoming calls in the
   *    meantime. Two paths disagreeing about the same session is the bug.
   *  - Remote termination stays enforceable where it is actually enforceable:
   *    server-side. Deleting the account, or changing the password with
   *    "terminate other sessions" (which re-derives the authKey this device's
   *    stored copy no longer matches), makes the re-login FAIL — and then these
   *    retries never succeed and the client honestly reports `revoked`. The
   *    server decides; the client only asks, and asks slowly.
   *  - A deliberate LOCAL sign-out is not touched by any of this: it wipes the
   *    identity and tears the engine down (headless main.ts teardownSession ⇒
   *    stop()), so there is no key left to re-authenticate with and no client
   *    left to try. Nothing here can resurrect a session the user signed out of
   *    on this device.
   *  - The cost is bounded: at most REAUTH_MAX_ROUNDS quick attempts, then one
   *    probe per REVOKED_RECHECK_MS (or per explicit user/host nudge).
   */
  private sessionRejected(): void {
    this.cancelRace();
    this.stopHeartbeat();
    if (this.socket) {
      this.detach(this.socket);
      try {
        this.socket.close();
      } catch {
        // already closed
      }
      this.socket = null;
    }
    // Resume state belongs to the dead session, and so do the buffered frames:
    // the server ends any live call when it revokes a session, so replaying
    // them into a fresh session would only re-assert state the server dropped.
    this.connId = null;
    this.lastSeq = 0;
    this.sendBuffer = [];
    this.endConnection(WS_CLOSE.SESSION_REVOKED);
    this.noteAuthRejected(this.liveToken);
    this.liveToken = null;
    this.scheduleRetry();
  }

  /**
   * Record that the server refused a session, and make the next race ask for a
   * fresh one. Counting DISTINCT tokens is what keeps the bound honest: all
   * racers in a round share one token, and a provider that returns the same
   * cached token has told us nothing new — we keep retrying (capped backoff)
   * until it rotates, rather than marching to `revoked` on no evidence.
   */
  private noteAuthRejected(token: string | null): void {
    this.forceFreshToken = true;
    if (token !== null && token !== this.rejectedToken) {
      this.rejectedToken = token;
      this.authRejects++;
    }
  }

  private resetAuthRejects(): void {
    this.authRejects = 0;
    this.rejectedToken = null;
    this.forceFreshToken = false;
    this.revokedLane = false;
  }

  // --- traffic counters (docs/10 §2 ws.lifetime) -----------------------------

  private countOut(c: ConnCounters | null, json: string): void {
    if (c === null) return;
    this.countOutBytes(c, utf8Bytes(json));
  }

  /** For a frame whose size is already known — an RPC frame is measured once,
   *  when it is built, and never scanned again. */
  private countOutBytes(c: ConnCounters | null, bytes: number): void {
    if (c === null) return;
    c.framesOut += 1;
    c.bytesOut += bytes;
  }

  private countIn(c: ConnCounters | null, data: unknown): void {
    if (c === null || typeof data !== 'string') return;
    c.framesIn += 1;
    c.bytesIn += utf8Bytes(data);
  }

  /**
   * Close out the live connection's record and release everything that was
   * riding it. Every path that loses the socket goes through here — server
   * close, heartbeat give-up, revocation, `stop()` — so a pending tunnelled
   * request can never outlive the socket it was sent on.
   */
  private endConnection(code: number | null): void {
    if (this.conn !== null) {
      this.conn.endedAt = this.deps.timers.now();
      this.conn.closeCode = code;
      this.lastConn = this.conn;
      this.conn = null;
    }
    this.failPendingRpc();
  }

  // --- REST over the socket --------------------------------------------------

  /** Socket in a state to carry a frame right now. */
  private rpcWritable(): boolean {
    return (
      !this.stopped &&
      this.status === 'connected' &&
      this.socket !== null &&
      this.socket.readyState === WS_OPEN
    );
  }

  /**
   * …and worth offering the caller. Two things beyond the socket's own state
   * take the tunnel away:
   *
   *  - a stand-down, because a server that answers no `sys.rpc` must not cost
   *    every REST call a deadline (RPC_FAIL_STREAK, claimRpcError);
   *  - a call. This socket is the call's signaling path, and a tunnelled
   *    request buys a REST call a few hundred bytes at the risk of putting up
   *    to 32 KB of inbound response (the server's result ceiling) ahead of a
   *    `call.ice` — plus, on a server one deploy behind, an `error` frame the
   *    engine reads as a refusal of our invite. REST falls back to HTTPS for
   *    the length of the call; a call never degrades for REST.
   */
  private rpcReady(): boolean {
    return this.rpcWritable() && this.activeCalls.size === 0 && !this.rpcCold();
  }

  /** Standing down, and against the endpoint we are actually on. */
  private rpcCold(): boolean {
    return this.rpcColdFor === this.rpcConnUrl && this.deps.timers.now() < this.rpcColdUntil;
  }

  /** Bytes the host says are still queued on the socket, when it says at all
   *  (`WebSocketLike` does not require it; browsers and `ws` provide it, the
   *  native bridge sockets do not — which is why RPC_MAX_INFLIGHT_BYTES, and
   *  not this, is what actually bounds the exposure on mobile). */
  private bufferedBytes(): number {
    const amount = (this.socket as { bufferedAmount?: unknown } | null)?.bufferedAmount;
    return typeof amount === 'number' ? amount : 0;
  }

  /**
   * Track the calls this socket is carrying, from the frames themselves — no
   * wiring, so nothing can ship with the gate unconnected, and the state is
   * per connection because `call.sync` (sent by the server on EVERY connect)
   * is authoritative and re-derives it.
   */
  private noteCallFrame(type: string, payload: unknown): void {
    if (type === 'call.sync') {
      const live = (payload as CallSyncPayload | null)?.call ?? null;
      this.activeCalls.clear();
      if (live !== null) this.addActiveCall(live.callId);
      return;
    }
    const callId = callIdOf(payload);
    if (callId === null) return;
    if (CALL_OVER_FRAMES.has(type)) {
      this.activeCalls.delete(callId);
      return;
    }
    if (CALL_LIVE_FRAMES.has(type)) this.addActiveCall(callId);
  }

  private addActiveCall(callId: string): void {
    const wasIdle = this.activeCalls.size === 0;
    this.activeCalls.add(callId);
    // Whatever REST had waiting is not going out on this socket now: settle it
    // unwritten so the caller reaches HTTPS immediately instead of sitting out
    // the deadline for a frame that will never be written.
    if (wasIdle) this.evictQueuedRpc();
  }

  /**
   * A bare `error` frame arriving while our own tunnelled request is
   * outstanding on a socket that has never answered one.
   *
   * A server one deploy behind answers an unknown frame type with exactly this
   * and keeps the socket (backend ws handler `dispatch` default), and the call
   * engine reads a bare error that lands while it is dialing as the server
   * refusing OUR invite — so passing this one on would end a live call because
   * a contacts screen refreshed. Attribution is not a guess: nothing else on
   * this connection has drawn a `sys.rpc.result`, and we have a `sys.rpc`
   * outstanding. The tunnel stands down and the frame goes no further.
   *
   * The residual risk is the mirror image — swallowing an error frame that was
   * genuinely about a call — and the prime directive settles which way to err:
   * a call that keeps its own timers beats a call ended on an ambiguous frame.
   */
  private claimRpcError(): boolean {
    if (this.rpcProven || this.rpcInflight === 0) return false;
    this.standDownRpc();
    for (const pending of [...this.rpcPending.values()]) {
      // Written ones are honestly `sent`: this server will not answer them, but
      // the wire cannot prove it never saw them, so the replay rule still runs
      // on the fact rather than on the inference.
      if (pending.sent) this.settleRpc(pending, { ok: false, failure: 'unavailable', sent: true });
    }
    return true;
  }

  /**
   * Tunnel one REST request. Never throws and never hangs: it resolves either
   * with the server's answer or with a failure that says whether the frame was
   * written, which is what the caller needs to decide whether re-issuing it
   * over HTTPS would be a replay (api/client.ts).
   */
  private sendRpc(request: RpcRequest): Promise<RpcOutcome> {
    if (!this.rpcReady()) return Promise.resolve(declined('unavailable'));
    // The protocol's own path rule, borrowed rather than restated (CLAUDE.md):
    // a path the server would reject as a protocol violation must never be
    // framed — on a server that closes the socket over one, that would cost the
    // connection a call is on. Declined unwritten, so HTTPS carries it.
    if (!SysRpcPayload.shape.path.safeParse(request.path).success) {
      return Promise.resolve(declined('unsent'));
    }
    const id = String(++this.rpcSeq);
    const frame: RpcFrame = {
      type: 'sys.rpc',
      seq: 0,
      payload: {
        id,
        method: request.method,
        path: request.path,
        ...(request.body !== undefined ? { body: request.body } : {}),
      },
    };
    const json = JSON.stringify(frame);
    const bytes = utf8Bytes(json);
    if (bytes > RPC_MAX_REQUEST_BYTES) return Promise.resolve(declined('oversize'));
    if (this.rpcQueue.length >= RPC_QUEUE_LIMIT) return Promise.resolve(declined('unsent'));
    return new Promise<RpcOutcome>((resolve) => {
      const pending: PendingRpc = {
        id,
        frame: json,
        bytes,
        settle: resolve,
        timer: null,
        sent: false,
        settled: false,
      };
      pending.timer = this.deps.timers.setTimeout(
        () => this.settleRpc(pending, { ok: false, failure: 'timeout', sent: pending.sent }),
        RPC_DEADLINE_MS,
      );
      this.rpcPending.set(id, pending);
      this.rpcQueue.push(pending);
      this.drainRpc();
    });
  }

  /**
   * Write what the socket can take. Three things hold a request back, and none
   * of them may ever hold a SIGNALING frame back (`send` writes straight
   * through): the inflight cap, so a burst of REST cannot own the socket a call
   * is on; the bytes those inflight requests already put on the wire, which is
   * the same bound expressed in the unit that actually matters on a slow link
   * and the only one that survives a host with no `bufferedAmount`; and the
   * host's own outbound queue when it does report one, because bytes already
   * waiting there are bytes a call's ICE would sit behind.
   */
  private drainRpc(): void {
    if (this.rpcDraining) return;
    this.rpcDraining = true;
    try {
      while (this.rpcQueue.length > 0 && this.rpcInflight < RPC_MAX_INFLIGHT) {
        const socket = this.socket;
        if (socket === null || !this.rpcReady()) break;
        // …but never deadlock: with nothing outstanding, the request itself is
        // the whole budget (RPC_MAX_REQUEST_BYTES is the smaller ceiling).
        const queued = this.rpcQueue[0];
        if (
          queued !== undefined &&
          this.rpcInflight > 0 &&
          this.rpcInflightBytes + queued.bytes > RPC_MAX_INFLIGHT_BYTES
        ) {
          break;
        }
        if (this.bufferedBytes() > RPC_BACKPRESSURE_BYTES) break;
        const next = this.rpcQueue.shift();
        if (next === undefined) break;
        if (next.settled) continue;
        // Booked as written BEFORE the write, and undone only if the host
        // refuses it. `sent` decides whether a caller may issue the request
        // again, so it may never under-report: a host that delivers the answer
        // (or an error frame) synchronously from inside send() would otherwise
        // settle a request that IS on the wire as one that never left.
        next.sent = true;
        this.rpcInflight += 1;
        this.rpcInflightBytes += next.bytes;
        try {
          socket.send(next.frame);
        } catch {
          // The socket refused the write, so nothing left this device (a
          // WebSocket that cannot write throws rather than queueing): the
          // caller may issue it over HTTPS without that being a replay.
          next.sent = false;
          this.rpcInflight = Math.max(0, this.rpcInflight - 1);
          this.rpcInflightBytes = Math.max(0, this.rpcInflightBytes - next.bytes);
          this.settleRpc(next, { ok: false, failure: 'unsent', sent: false });
          continue;
        }
        this.countOutBytes(this.conn, next.bytes);
      }
    } finally {
      this.rpcDraining = false;
    }
    if (this.rpcQueue.length > 0) this.armRpcDrain();
    else this.clearRpcDrain();
  }

  private armRpcDrain(): void {
    if (this.rpcDrainTimer !== null) return;
    this.rpcDrainTimer = this.deps.timers.setTimeout(() => {
      this.rpcDrainTimer = null;
      this.drainRpc();
    }, RPC_DRAIN_MS);
  }

  private clearRpcDrain(): void {
    if (this.rpcDrainTimer === null) return;
    this.deps.timers.clearTimeout(this.rpcDrainTimer);
    this.rpcDrainTimer = null;
  }

  /**
   * A correlated answer arrived — which by itself proves the server routes
   * `sys.rpc`, whatever it then says.
   *
   * Two of the statuses it can carry are the GATEWAY's, not the route's, and
   * handing them to the caller as the route's answer would break screens that
   * plain HTTPS serves perfectly well:
   *
   *  - 501: no dispatcher installed (backend ws/rpc.go, answered before
   *    dispatch — an explicitly supported server state). The route did not run,
   *    and every later request would meet the same wall, so the transport
   *    stands down and the caller retries over HTTPS.
   *  - 502: the response did not fit the server's 32 KB result ceiling
   *    (rpcws `TooLarge`), or could not be represented in a frame at all. The
   *    route DID run, so `sent` stays true and only an idempotent request may
   *    go again — which is exactly the case that matters, an unpaginated
   *    `GET /friends` on an account with hundreds of them. Not a stand-down:
   *    it says something about one route's response, not about the tunnel.
   *
   * No REST route in this API answers either status (they are `net/http`
   * gateway semantics and the server never writes them from a handler), so
   * nothing legitimate is being stolen from the caller here.
   */
  private resolveRpc(payload: RpcResultPayload): void {
    this.rpcProven = true;
    const pending = this.rpcPending.get(payload.id);
    if (!pending) return; // a late answer to something already given up on
    if (payload.status === 501) {
      this.standDownRpc();
      this.settleRpc(pending, { ok: false, failure: 'unsupported', sent: pending.sent });
      return;
    }
    if (payload.status === 502) {
      this.settleRpc(pending, { ok: false, failure: 'oversize', sent: pending.sent });
      return;
    }
    this.settleRpc(pending, {
      ok: true,
      status: payload.status,
      ...(payload.body !== undefined ? { body: payload.body } : {}),
    });
  }

  private settleRpc(pending: PendingRpc, outcome: RpcOutcome): void {
    if (pending.settled) return;
    pending.settled = true;
    this.deps.timers.clearTimeout(pending.timer);
    this.rpcPending.delete(pending.id);
    const queued = this.rpcQueue.indexOf(pending);
    if (queued >= 0) this.rpcQueue.splice(queued, 1);
    if (pending.sent) {
      this.rpcInflight = Math.max(0, this.rpcInflight - 1);
      this.rpcInflightBytes = Math.max(0, this.rpcInflightBytes - pending.bytes);
    }
    if (outcome.ok) this.rpcFails = 0;
    // Only a request the server could actually have seen is evidence about the
    // server. One that timed out in the queue — held back by a call or by
    // congestion — says nothing, and counting it would stand the tunnel down
    // for the very reason it was busy.
    else if (outcome.failure === 'timeout' && pending.sent) this.noteRpcFailure();
    pending.settle(outcome);
    if (this.rpcQueue.length > 0) this.drainRpc();
  }

  /**
   * A live socket that does not answer is the signature of a server without the
   * `sys.rpc` route. Standing down for a cooldown keeps that from taxing every
   * REST call with a deadline it will always lose; HTTPS carries them meanwhile,
   * exactly as before this transport existed.
   */
  private noteRpcFailure(): void {
    this.rpcFails += 1;
    if (this.rpcFails < RPC_FAIL_STREAK) return;
    this.standDownRpc();
  }

  /** Stop offering the tunnel on this endpoint for RPC_COOLDOWN_MS, and release
   *  anything still queued for it so those callers reach HTTPS now. */
  private standDownRpc(): void {
    this.rpcFails = 0;
    this.rpcColdFor = this.rpcConnUrl;
    this.rpcColdUntil = this.deps.timers.now() + RPC_COOLDOWN_MS;
    this.evictQueuedRpc();
  }

  /** Settle every request still waiting for a write. Unwritten is `sent: false`
   *  — provably not a replay, so any method may go over HTTPS. */
  private evictQueuedRpc(): void {
    for (const pending of [...this.rpcQueue]) {
      this.settleRpc(pending, { ok: false, failure: 'unsent', sent: false });
    }
  }

  /**
   * Release every request riding the dead socket. Nothing is re-queued for the
   * next connection: a request already written may have reached the server, and
   * re-sending it after a reconnect would be exactly the silent replay that
   * `POST /auth/refresh` must never suffer (docs/03 §3). The caller decides,
   * with `sent` in hand.
   */
  private failPendingRpc(): void {
    for (const pending of [...this.rpcPending.values()]) {
      this.settleRpc(pending, { ok: false, failure: 'closed', sent: pending.sent });
    }
    this.rpcPending.clear();
    this.rpcQueue = [];
    this.rpcInflight = 0;
    this.rpcInflightBytes = 0;
    // Per-connection evidence dies with the connection: a fresh socket must not
    // inherit a fail streak, and must re-prove that it routes `sys.rpc` before
    // an ambiguous `error` frame is attributed to us (claimRpcError). The
    // stand-down deadline is deliberately NOT reset — it is keyed to the
    // endpoint, and reconnecting to the same one does not make its server newer.
    this.rpcFails = 0;
    this.rpcProven = false;
    // Same per-connection rule for the credential: the next socket presents its
    // own `sys.auth`, and may land on a different deployment than this one.
    this.reauthSupported = true;
    this.reauthErrorUntil = Number.NEGATIVE_INFINITY;
    this.activeCalls.clear();
    this.clearRpcDrain();
  }

  private parseFrame(data: unknown): WsFrame | null {
    if (typeof data !== 'string') return null;
    let json: unknown;
    try {
      json = JSON.parse(data);
    } catch {
      return null;
    }
    const parsed = WsFrame.safeParse(json);
    return parsed.success ? parsed.data : null;
  }

  private detach(ws: WebSocketLike): void {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
  }

  private persist(): void {
    this.deps.persist?.({
      list: this.list,
      ...(this.lastGoodUrl !== undefined ? { lastGoodUrl: this.lastGoodUrl } : {}),
    });
  }

  private setStatus(status: SignalingStatus): void {
    // In the revoked lane every attempt is a re-auth probe, not a reconnect:
    // keep reporting `revoked` until one actually succeeds, so the host shows
    // "session terminated" instead of a hopeful spinner over a session the
    // server keeps refusing. `idle` (stop) and `connected` (success) still win.
    const next =
      this.revokedLane && status !== 'connected' && status !== 'idle' ? 'revoked' : status;
    if (this.status === next) return;
    this.status = next;
    this.deps.onStatus(next);
  }
}
